import React, { useState, useEffect, useMemo } from 'react';
import { FiPlus, FiFilter, FiSearch, FiEdit2, FiTrash2, FiCheckCircle, FiClock, FiXCircle, FiTrendingDown, FiTrendingUp, FiPaperclip, FiCopy, FiDownload } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import despesaService from '../services/despesaService';
import toast from 'react-hot-toast';
import DespesaModal from '../components/DespesaModal';
import { CATEGORIA_LABELS, SUBCATEGORIA_LABELS, STATUS_LABELS, FORMA_PAGAMENTO_LABELS, MESES_NOMES } from '../utils/despesaLabels';

const DespesasPage = () => {
    const today = new Date();
    const [mes, setMes] = useState(today.getMonth() + 1);
    const [ano, setAno] = useState(today.getFullYear());
    const [despesas, setDespesas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exportando, setExportando] = useState(false);
    const [activeTab, setActiveTab] = useState('TODAS');
    const [searchTerm, setSearchTerm] = useState('');

    // Checkbox State (Baixa em Massa)
    const [selecionados, setSelecionados] = useState([]);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [despesaEdit, setDespesaEdit] = useState(null);

    const meses = [
        { num: 1, nome: 'Janeiro' }, { num: 2, nome: 'Fevereiro' }, { num: 3, nome: 'Março' },
        { num: 4, nome: 'Abril' }, { num: 5, nome: 'Maio' }, { num: 6, nome: 'Junho' },
        { num: 7, nome: 'Julho' }, { num: 8, nome: 'Agosto' }, { num: 9, nome: 'Setembro' },
        { num: 10, nome: 'Outubro' }, { num: 11, nome: 'Novembro' }, { num: 12, nome: 'Dezembro' }
    ];

    const carregarDespesas = async () => {
        setLoading(true);
        try {
            const data = await despesaService.listarPorMesAno(ano, mes);
            setDespesas(data);
            setSelecionados([]); // Limpa a seleção ao trocar de mês
        } catch (error) {
            toast.error('Erro ao carregar despesas.');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        carregarDespesas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mes, ano]);

    // Limpa a seleção ao trocar de aba (categoria)
    useEffect(() => {
        setSelecionados([]);
    }, [activeTab]);

    const handleSaveDespesa = async (dados, id) => {
        if (id) {
            await despesaService.atualizarDespesa(id, dados);
            toast.success('Despesa atualizada com sucesso!');
        } else {
            await despesaService.criarDespesa(dados);
            toast.success('Nova despesa registrada!');
        }
        carregarDespesas();
    };

    const handleMarcarPago = async (id) => {
        try {
            await despesaService.marcarComoPago(id);
            toast.success('Despesa baixada/paga!');
            carregarDespesas();
        } catch (e) {
            toast.error('Erro ao pagar despesa.');
        }
    };

    // -- Lógica de Filtro (Categoria + Pesquisa) --
    const despesasFiltradas = despesas.filter(d => {
        const matchCategoria = activeTab === 'TODAS' || d.categoria === activeTab;
        if (!matchCategoria) return false;
        if (!searchTerm.trim()) return true;
        const termo = searchTerm.toLowerCase();
        return (
            (d.descricao || '').toLowerCase().includes(termo) ||
            (d.categoria || '').toLowerCase().includes(termo) ||
            (d.subcategoria || '').toLowerCase().includes(termo) ||
            (d.usuarioNome || '').toLowerCase().includes(termo)
        );
    });

    const toggleSelecionado = (id) => {
        setSelecionados(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const toggleTodos = () => {
        const idsPendentes = despesasFiltradas
            .filter(d => d.status === 'PENDENTE')
            .map(d => d.id);

        if (selecionados.length === idsPendentes.length && idsPendentes.length > 0) {
            setSelecionados([]);
        } else {
            setSelecionados(idsPendentes);
        }
    };

    const handleBaixaEmMassa = async () => {
        if (selecionados.length === 0) return;

        const confirmacao = window.confirm(`Deseja dar baixa nas ${selecionados.length} despesas selecionadas?`);
        if (!confirmacao) return;

        try {
            await despesaService.marcarComoPagoEmMassa(selecionados);
            toast.success(`${selecionados.length} despesas marcadas como pagas!`);
            setSelecionados([]);
            carregarDespesas();
        } catch (error) {
            toast.error("Erro ao processar baixa em massa.");
        }
    };
    // --------------------------------

    const handleExcluir = async (id) => {
        if (!window.confirm("Tem certeza que deseja excluir esta despesa? A projeção do DRE será recalculada.")) return;
        try {
            await despesaService.deletarDespesa(id);
            toast.success('Despesa removida.');
            carregarDespesas();
        } catch (e) {
            toast.error('Erro ao excluir despesa.');
        }
    };

    const handleReplicar = async () => {
        if (!window.confirm("Deseja copiar as despesas fixas deste mês para o mês seguinte?")) return;

        try {
            await despesaService.replicarDespesas(mes, ano);
            toast.success('Despesas fixas replicadas com sucesso!');

            if (window.confirm("Deseja avançar a tela para o próximo mês para ver os novos lançamentos?")) {
                let nextMes = mes + 1;
                let nextAno = ano;
                if (nextMes > 12) {
                    nextMes = 1;
                    nextAno++;
                }
                setMes(nextMes);
                setAno(nextAno);
            } else {
                carregarDespesas();
            }
        } catch (error) {
            toast.error('Erro ao replicar despesas.');
            console.error(error);
        }
    };

    const handleExportarRelatorioAnual = async () => {
        setExportando(true);
        try {
            const dados = await despesaService.listarPorAno(ano);
            if (!dados || dados.length === 0) {
                toast.error(`Nenhuma despesa encontrada em ${ano}.`);
                return;
            }

            const mesDaDespesa = (d) => parseInt(d.dataVencimento.split('-')[1], 10) - 1;

            // Aba Resumo: canceladas ficam de fora, mesma regra dos KPIs da tela
            const ativas = dados.filter(d => d.status !== 'CANCELADO');

            const somaPorMes = (filtro) => {
                const porMes = Array(12).fill(0);
                ativas.filter(filtro).forEach(d => { porMes[mesDaDespesa(d)] += d.valor; });
                return porMes;
            };
            const comTotal = (label, porMes) => [label, ...porMes, porMes.reduce((a, b) => a + b, 0)];

            const linhas = [['Categoria', ...MESES_NOMES, 'Total']];
            Object.keys(CATEGORIA_LABELS)
                .filter(cat => ativas.some(d => d.categoria === cat))
                .forEach(cat => {
                    linhas.push(comTotal(CATEGORIA_LABELS[cat], somaPorMes(d => d.categoria === cat)));
                });
            linhas.push(comTotal('Total Geral', somaPorMes(() => true)));
            linhas.push([]);
            linhas.push(comTotal('Total Pago', somaPorMes(d => d.status === 'PAGO')));
            linhas.push(comTotal('Total Pendente', somaPorMes(d => d.status === 'PENDENTE')));

            const wsResumo = XLSX.utils.aoa_to_sheet(linhas);
            wsResumo['!cols'] = [{ wch: 22 }, ...MESES_NOMES.map(() => ({ wch: 12 })), { wch: 14 }];

            // Aba Despesas: listagem completa do ano, canceladas incluídas para auditoria
            const detalhe = dados.map(d => ({
                'Mês': MESES_NOMES[mesDaDespesa(d)],
                'Vencimento': formatarData(d.dataVencimento),
                'Pagamento': formatarData(d.dataPagamento),
                'Descrição': d.descricao,
                'Categoria': CATEGORIA_LABELS[d.categoria] || d.categoria,
                'Subcategoria': SUBCATEGORIA_LABELS[d.subcategoria] || d.subcategoria || '--',
                'Status': STATUS_LABELS[d.status] || d.status,
                'Forma de Pagamento': FORMA_PAGAMENTO_LABELS[d.formaPagamento] || d.formaPagamento || '--',
                'Parcela': d.numeroParcela ? `${d.numeroParcela}/${d.totalParcelas}` : '--',
                'Responsável': d.usuarioNome || '--',
                'Valor (R$)': d.valor
            }));
            const wsDetalhe = XLSX.utils.json_to_sheet(detalhe);
            wsDetalhe['!cols'] = [
                { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 40 }, { wch: 18 }, { wch: 16 },
                { wch: 10 }, { wch: 18 }, { wch: 8 }, { wch: 16 }, { wch: 12 }
            ];

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, wsResumo, 'Resumo');
            XLSX.utils.book_append_sheet(workbook, wsDetalhe, 'Despesas');
            XLSX.writeFile(workbook, `Relatorio_Despesas_${ano}.xlsx`);

            toast.success(`Relatório de ${ano} exportado! (${dados.length} despesas)`);
        } catch (error) {
            toast.error('Erro ao exportar relatório anual.');
            console.error(error);
        } finally {
            setExportando(false);
        }
    };

    const openEdit = (d) => {
        setDespesaEdit(d);
        setIsModalOpen(true);
    };

    const openNew = () => {
        setDespesaEdit(null);
        setIsModalOpen(true);
    };

    const handleDuplicar = (despesa) => {
        // Clone sem id (backend entende como criação), reseta status e remove parcelamento
        const clone = {
            ...despesa,
            id: undefined,
            status: 'PENDENTE',
            dataPagamento: null,
            formaPagamento: null,
            totalParcelas: null,
            grupoParcelamento: null,
        };
        setDespesaEdit(clone);
        setIsModalOpen(true);
    };

    // Utils
    const formatarMoeda = (valor) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
    const formatarData = (dataStr) => {
        if (!dataStr) return '--';
        const [y, m, d] = dataStr.split('-');
        return `${d}/${m}/${y}`;
    };

    // -- Lógica de Análise Vertical (Distribuição de Gastos) --
    const analiseVertical = useMemo(() => {
        const ativas = despesas.filter(d => d.status !== 'CANCELADO');
        const sumTotal = ativas.reduce((acc, curr) => acc + curr.valor, 0);

        const groups = ativas.reduce((acc, curr) => {
            acc[curr.categoria] = (acc[curr.categoria] || 0) + curr.valor;
            return acc;
        }, {});

        const categoryColors = {
            'DESPESA_FIXA': 'bg-blue-500',
            'COMPRA_NACIONAL': 'bg-indigo-500',
            'DESPESA_VARIAVEL': 'bg-purple-500',
            'IMPOSTO': 'bg-red-500',
            'PESSOAL': 'bg-amber-500',
            'INVESTIMENTO': 'bg-emerald-500',
            'OUTROS': 'bg-gray-400'
        };

        const labels = {
            'DESPESA_FIXA': 'Fixas',
            'COMPRA_NACIONAL': 'Compras Nacionais',
            'DESPESA_VARIAVEL': 'Variáveis',
            'IMPOSTO': 'Impostos',
            'PESSOAL': 'Pessoal',
            'INVESTIMENTO': 'Investimentos',
            'OUTROS': 'Outros'
        };

        const distribuicao = Object.keys(groups).map(cat => ({
            id: cat,
            label: labels[cat] || cat,
            valor: groups[cat],
            porcentagem: sumTotal > 0 ? (groups[cat] / sumTotal) * 100 : 0,
            color: categoryColors[cat] || 'bg-gray-400'
        })).sort((a, b) => b.valor - a.valor);

        return { sumTotal, distribuicao };
    }, [despesas]);

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PAGO': return <span className="bg-slate-100 text-emerald-700 border border-slate-200 text-[10px] uppercase font-semibold px-2.5 py-1 rounded-md flex items-center gap-1 w-max"><FiCheckCircle size={11} /> Pago</span>;
            case 'PENDENTE': return <span className="bg-slate-100 text-amber-700 border border-slate-200 text-[10px] uppercase font-semibold px-2.5 py-1 rounded-md flex items-center gap-1 w-max"><FiClock size={11} /> Pendente</span>;
            case 'CANCELADO': return <span className="bg-slate-100 text-red-600 border border-slate-200 text-[10px] uppercase font-semibold px-2.5 py-1 rounded-md flex items-center gap-1 w-max"><FiXCircle size={11} /> Cancelado</span>;
            default: return status;
        }
    };

    const getCategoriaBadge = (cat, subcat) => {
        const labels = {
            'DESPESA_FIXA': 'Fixa',
            'COMPRA_NACIONAL': 'Compra Nacional',
            'DESPESA_VARIAVEL': 'Variável',
            'IMPOSTO': 'Imposto',
            'PESSOAL': 'Pessoal',
            'INVESTIMENTO': 'Investimento',
            'OUTROS': 'Outros'
        };

        const subLabels = {
            'OPERACIONAL': 'Operacional',
            'SISTEMAS': 'Sistemas',
            'ASSESSORIAS': 'Assessorias',
            'ALIMENTACAO': 'Alimentação',
            'LIMPEZA': 'Limpeza',
            'SEGURANCA': 'Segurança',
            'EXTRAS': 'Extras',
            'PLATAFORMAS_ADS_FULL': 'Ads / Plataformas'
        };

        return (
            <div className="flex flex-col gap-1 items-start">
                <span className="bg-white text-slate-600 border border-slate-200 text-[10px] uppercase font-semibold px-2 py-0.5 rounded-md">{labels[cat] || cat}</span>
                {subcat && (
                    <span className="text-[10px] text-slate-400 font-medium">{subLabels[subcat] || subcat}</span>
                )}
            </div>
        );
    };

    // KPIs
    const totalGeral = despesasFiltradas.reduce((acc, curr) => acc + (curr.status !== 'CANCELADO' ? curr.valor : 0), 0);
    const totalPago = despesasFiltradas.reduce((acc, curr) => acc + (curr.status === 'PAGO' ? curr.valor : 0), 0);
    const totalPendente = despesasFiltradas.reduce((acc, curr) => acc + (curr.status === 'PENDENTE' ? curr.valor : 0), 0);

    const categoryTabs = [
        { id: 'TODAS', label: 'Todas' },
        { id: 'DESPESA_FIXA', label: 'Despesas Fixas' },
        { id: 'COMPRA_NACIONAL', label: 'Compras Nacionais' },
        { id: 'DESPESA_VARIAVEL', label: 'Despesas Variáveis' },
        { id: 'IMPOSTO', label: 'Impostos' },
        { id: 'PESSOAL', label: 'Pessoal' },
        { id: 'INVESTIMENTO', label: 'Investimentos' },
        { id: 'OUTROS', label: 'Outros' }
    ];

    return (
        <div className="space-y-6 animate-in fade-in duration-500 pb-12">

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 rounded-lg shadow-sm border border-slate-200 gap-4">
                <div>
                    <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Controle Financeiro</h1>
                    <p className="text-slate-500 font-medium text-sm mt-1">Gerencie e dê baixa nas despesas — integrado ao Balancete</p>
                </div>

                <div className="flex bg-white p-1 rounded-md border border-slate-200">
                    <select value={mes} onChange={(e) => setMes(Number(e.target.value))} className="bg-transparent font-medium text-slate-700 py-1.5 px-3 outline-none cursor-pointer text-sm">
                        {meses.map(m => <option key={m.num} value={m.num}>{m.nome}</option>)}
                    </select>
                    <div className="w-px bg-slate-200 my-1.5 mx-1"></div>
                    <select value={ano} onChange={(e) => setAno(Number(e.target.value))} className="bg-transparent font-medium text-slate-700 py-1.5 px-3 outline-none cursor-pointer text-sm">
                        {[ano - 1, ano, ano + 1].map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-5 rounded-lg shadow-sm border border-slate-200">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Total de Despesas ({meses.find(m => m.num === mes).nome})</span>
                    <h2 className="text-2xl font-bold text-slate-900 tracking-tight mt-2 font-mono">{formatarMoeda(totalGeral)}</h2>
                </div>
                <div className="bg-white p-5 rounded-lg shadow-sm border border-slate-200">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">A Pagar / Pendente</span>
                    <h2 className="text-2xl font-bold text-amber-600 tracking-tight mt-2 font-mono">{formatarMoeda(totalPendente)}</h2>
                </div>
                <div className="bg-white p-5 rounded-lg shadow-sm border border-slate-200">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Já Pago (Em Caixa)</span>
                    <h2 className="text-2xl font-bold text-emerald-600 tracking-tight mt-2 font-mono">{formatarMoeda(totalPago)}</h2>
                </div>
            </div>

            {analiseVertical.sumTotal > 0 && (
                <div className="bg-white p-5 rounded-lg shadow-sm border border-slate-200 flex flex-col pt-4">
                    <h3 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-4">Distribuição por Categoria</h3>

                    <div className="w-full flex h-2 rounded-full overflow-hidden mb-5 bg-slate-100">
                        {analiseVertical.distribuicao.map(item => (
                            <div
                                key={item.id}
                                className={`${item.color} h-full transition-all duration-500 ease-out`}
                                style={{ width: `${item.porcentagem}%` }}
                                title={`${item.label}: ${item.porcentagem.toFixed(1)}%`}
                            ></div>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                        {analiseVertical.distribuicao.map(item => (
                            <div key={item.id} className="bg-white p-3 rounded-md border border-slate-200 flex flex-col justify-between hover:border-slate-300 transition-all duration-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className={`w-2 h-2 rounded-full ${item.color} flex-shrink-0`}></div>
                                    <span className="text-[10px] font-semibold text-slate-500 uppercase truncate">{item.label}</span>
                                </div>
                                <div>
                                    <h4 className="text-lg font-bold text-slate-900 tracking-tight leading-none mb-0.5">{item.porcentagem.toFixed(1)}%</h4>
                                    <p className="text-[10px] text-slate-400 font-medium">{formatarMoeda(item.valor)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex overflow-x-auto border-b border-slate-200 mb-0 bg-white px-2 pt-2">
                {categoryTabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`whitespace-nowrap px-5 py-3.5 text-sm font-medium transition-all border-b-2 flex-shrink-0 ${
                            activeTab === tab.id
                                ? 'border-slate-900 text-slate-900'
                                : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden relative">

                {/* PAINEL DE AÇÃO EM MASSA (Aparece quando há itens selecionados) */}
                {selecionados.length > 0 && (
                    <div className="absolute top-0 left-0 right-0 bg-slate-50 border-b border-slate-200 p-3 md:p-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-3 z-10 animate-in slide-in-from-top-4 duration-300">
                        <div className="flex items-center gap-2">
                            <span className="flex items-center justify-center bg-slate-900 text-white w-6 h-6 rounded-full text-xs font-bold">
                                {selecionados.length}
                            </span>
                            <span className="font-semibold text-slate-800 text-sm">despesas selecionadas</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setSelecionados([])}
                                className="text-slate-500 hover:text-slate-700 text-sm font-medium transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleBaixaEmMassa}
                                className="bg-slate-900 hover:bg-slate-800 text-white px-5 py-2 rounded-md font-semibold shadow-sm transition-all active:scale-95 flex items-center gap-2 text-sm"
                            >
                                <FiCheckCircle size={16} />
                                Confirmar Baixa em Massa
                            </button>
                        </div>
                    </div>
                )}

                <div className="px-5 py-4 border-b border-slate-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-3 bg-white">
                    <div className="relative w-full md:w-auto">
                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar despesa..." className="w-full md:w-auto pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-md text-sm outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 transition-all min-w-[250px]" />
                    </div>
                    <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                        <button onClick={handleExportarRelatorioAnual} disabled={exportando} className="flex justify-center items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-md font-medium hover:bg-slate-50 transition-colors active:scale-95 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                            <FiDownload size={16} /> {exportando ? 'Exportando...' : `Relatório ${ano}`}
                        </button>
                        <button onClick={handleReplicar} className="flex justify-center items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-md font-medium hover:bg-slate-50 transition-colors active:scale-95 text-sm">
                            Replicar Fixas
                        </button>
                        <button onClick={openNew} className="flex justify-center items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-md font-medium hover:bg-slate-800 transition-colors active:scale-95 text-sm">
                            <FiPlus size={16} /> Nova Despesa
                        </button>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-sm">
                        <thead>
                            <tr className="bg-white border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
                                <th className="pl-3 pr-1 py-2.5 w-8">
                                    <input
                                        type="checkbox"
                                        onChange={toggleTodos}
                                        checked={selecionados.length > 0 && selecionados.length === despesasFiltradas.filter(d => d.status === 'PENDENTE').length}
                                        className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400 cursor-pointer"
                                    />
                                </th>
                                <th className="px-2 py-2.5 font-semibold whitespace-nowrap">Status</th>
                                <th className="px-2 py-2.5 font-semibold whitespace-nowrap">Descrição</th>
                                <th className="px-2 py-2.5 font-semibold whitespace-nowrap">Categoria</th>
                                <th className="px-2 py-2.5 font-semibold whitespace-nowrap">Vencimento</th>
                                <th className="px-2 py-2.5 font-semibold whitespace-nowrap">Pagamento</th>
                                <th className="px-2 py-2.5 font-semibold whitespace-nowrap">Responsável</th>
                                <th className="px-2 py-2.5 font-semibold text-right whitespace-nowrap">Valor</th>
                                <th className="px-3 py-2.5 font-semibold text-center whitespace-nowrap sticky right-0 bg-white z-10 border-l border-slate-200 shadow-[-4px_0_10px_-4px_rgba(0,0,0,0.05)]">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {loading ? (
                                <tr><td colSpan="9" className="px-3 py-8 text-center text-gray-400">Carregando despesas...</td></tr>
                            ) : despesasFiltradas.length === 0 ? (
                                <tr>
                                    <td colSpan="9" className="px-3 py-12 text-center">
                                        <div className="text-gray-300 text-5xl mb-3 flex justify-center"><FiTrendingDown /></div>
                                        <p className="text-gray-500 font-bold">Nenhuma despesa encontrada para esta categoria/mês.</p>
                                    </td>
                                </tr>
                            ) : (
                                despesasFiltradas.map((d) => (
                                    <tr key={d.id} className={`hover:bg-gray-50/50 transition-colors group ${d.status === 'CANCELADO' ? 'opacity-50' : ''} ${selecionados.includes(d.id) ? 'bg-blue-50/50' : ''}`}>
                                        <td className="pl-3 pr-1 py-3">
                                            <input
                                                type="checkbox"
                                                checked={selecionados.includes(d.id)}
                                                onChange={() => toggleSelecionado(d.id)}
                                                disabled={d.status !== 'PENDENTE'}
                                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-30 cursor-pointer"
                                            />
                                        </td>
                                        <td className="px-2 py-3 whitespace-nowrap">{getStatusBadge(d.status)}</td>
                                        <td className="px-2 py-3 font-semibold text-gray-800 max-w-[220px]" title={d.descricao}>
                                            <div className="flex items-center gap-1.5">
                                                <span className="truncate">{d.descricao}</span>
                                                {d.anexoUrl && (
                                                    <a href={d.anexoUrl} target="_blank" rel="noopener noreferrer" className="bg-blue-50 text-blue-600 p-1 rounded-md hover:bg-blue-100 transition-colors flex-shrink-0" title="Visualizar Anexo" onClick={(e) => e.stopPropagation()}>
                                                        <FiPaperclip size={13} />
                                                    </a>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-2 py-3 whitespace-nowrap">{getCategoriaBadge(d.categoria, d.subcategoria)}</td>
                                        <td className={`px-2 py-3 font-mono text-xs whitespace-nowrap ${d.status === 'PENDENTE' && new Date(d.dataVencimento) < new Date() ? 'text-red-500 font-bold' : 'text-gray-600'}`}>
                                            {formatarData(d.dataVencimento)}
                                        </td>
                                        <td className="px-2 py-3 font-mono text-xs text-gray-500 whitespace-nowrap">{formatarData(d.dataPagamento)}</td>
                                        <td className="px-2 py-3 text-xs font-semibold text-gray-400 whitespace-nowrap max-w-[100px] truncate" title={d.usuarioNome || '--'}>{d.usuarioNome || '--'}</td>
                                        <td className={`px-2 py-3 font-mono font-black text-right whitespace-nowrap ${d.status === 'PAGO' ? 'text-emerald-600' : 'text-gray-700'}`}>
                                            {formatarMoeda(d.valor)}
                                        </td>
                                        <td className={`px-2 py-3 whitespace-nowrap sticky right-0 z-10 border-l border-slate-200 shadow-[-4px_0_10px_-4px_rgba(0,0,0,0.05)] ${selecionados.includes(d.id) ? 'bg-blue-50 group-hover:bg-blue-100/60' : d.status === 'PAGO' ? 'bg-slate-50 group-hover:bg-slate-100' : d.status === 'CANCELADO' ? 'bg-white group-hover:bg-gray-50' : 'bg-white group-hover:bg-gray-50'}`}>
                                            <div className="flex items-center justify-center gap-1">
                                                {d.status === 'PENDENTE' && (
                                                    <button onClick={() => handleMarcarPago(d.id)} className="p-1 text-emerald-500 hover:bg-emerald-50 rounded-md transition-colors" title="Dar Baixa (Pagar)">
                                                        <FiCheckCircle size={15} />
                                                    </button>
                                                )}
                                                <button onClick={() => openEdit(d)} className="p-1 text-blue-500 hover:bg-blue-50 rounded-md transition-colors" title="Editar">
                                                    <FiEdit2 size={15} />
                                                </button>
                                                <button onClick={() => handleDuplicar(d)} className="p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 rounded-md transition-colors" title="Duplicar Despesa">
                                                    <FiCopy size={15} />
                                                </button>
                                                <button onClick={() => handleExcluir(d.id)} className="p-1 text-red-400 hover:bg-red-50 rounded-md transition-colors" title="Excluir">
                                                    <FiTrash2 size={15} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <DespesaModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSaveDespesa}
                despesaToEdit={despesaEdit}
                initialCategoria={activeTab === 'TODAS' ? 'DESPESA_FIXA' : activeTab}
            />
        </div>
    );
};

export default DespesasPage;